<template>
	<v-card-title v-if="title" class="d-flex align-center">
		{{ title }}
		<v-icon color="info" @click="load">$reload</v-icon>
	</v-card-title>
	<v-card-text>
		<v-form @submit.prevent="search" class="form-inline">
			<v-row class="">
				<v-col cols="12" class="d-flex flex-wrap align-center">
					<v-text-field
						outlined
						dense
						v-model="query.name"
						label="名称"
						hide-details
						class="search-field-name mr-1"
					/>
					<v-select
						outlined
						dense
						label="状态"
						v-model="query.status"
						:items="refs['RoastStatus']"
						hide-details
						class="mr-1"
						clearable
					></v-select>
					<v-chip
						v-if="query.recipe_id"
						class="mr-1"
						size="small"
						closable
						color="primary"
						variant="tonal"
						@click:close="clearRecipeFilter"
					>
						配方 #{{ query.recipe_id }}
					</v-chip>
					<v-chip
						v-if="query.bean_id"
						class="mr-1"
						size="small"
						closable
						color="brown"
						variant="tonal"
						@click:close="clearBeanFilter"
					>
						生豆 #{{ query.bean_id }}
					</v-chip>
					<v-chip
						v-if="query.no_recipe"
						class="mr-1"
						size="small"
						closable
						color="warning"
						variant="tonal"
						@click:close="clearNoRecipeFilter"
					>
						未关联配方（近1个月）
					</v-chip>
					<v-btn color="primary" @click="search" class="mr-1">
						<v-icon>$search</v-icon>
						查询
					</v-btn>
					<v-icon v-if="queryPairs.length > 0" class="mr-1" @click="reset">$close</v-icon>
				</v-col>
			</v-row>
		</v-form>
	</v-card-text>
	<v-data-table-server
		:headers="headers"
		:items="objects"
		@update:options="load"
		:items-length="parseInt(total)"
		:loading="loading"
		:show-select="showSelect"
		:single-select="singleSelect"
		v-model="selected"
		multi-sort
	>
		<template v-slot:top>
			<slot name=" batchActions" v-bind:selected="selected"></slot>
		</template>
		<template v-slot:item.status="{ item }">
			<v-chip size="small" :color="statusColor(item.status)" variant="tonal">
				{{ statusLabel(item.status) }}
			</v-chip>
		</template>
		<template v-slot:item.degree="{ item }">
			<roast-degree-badge v-if="item.degree" :degree="item.degree" />
			<span v-else class="text-medium-emphasis">—</span>
		</template>
		<template v-slot:item.roaster="{ item }">
			<span v-if="item.roaster?.nickname">{{ item.roaster.nickname }}</span>
			<span v-else class="text-medium-emphasis">—</span>
		</template>
		<template v-slot:item.name="{ item }">{{ item.name }}</template>
		<template v-slot:item.bean_id="{ item }">
			<span class="d-inline-flex align-center">
				{{ item.bean?.name }}
				<iconify-icon
					v-if="item.bean_id || item.bean?.id"
					icon="mdi:package-variant-closed"
					class="ml-1"
					width="18"
					style="color: #8f5a3c; cursor: pointer; flex-shrink: 0"
					title="库存 / 批次变动"
					@click.stop="goBeanInventory(item)"
				/>
			</span>
		</template>
		<template v-slot:item.recipe_id="{ item }">
			<template v-if="item.recipe_id">
				<v-chip size="small" color="primary" variant="tonal" :to="recipeLink(item)">
					#{{ item.recipe_id }}
				</v-chip>
			</template>
			<span v-else class="text-medium-emphasis">—</span>
		</template>
		<template v-slot:item.inqty="{ item }">
			<span class="text-medium-emphasis">生</span>
			<span style="color: green"> {{ item.inqty || 0 }}g</span>
			<span class="mx-1">→</span>
			<span class="text-medium-emphasis">熟</span>
			<span v-if="item.outqty" style="color: #6d4c41; font-weight: 600"> {{ item.outqty }}g</span>
			<span v-else class="text-medium-emphasis"> —</span>
		</template>
		<template v-slot:item.action="{ item }">
			<slot name="actions" v-bind:item="item"></slot>
		</template>

		<template v-slot:footer>
			<slot name="batchActions" v-bind:selected="selected"></slot>
		</template>
	</v-data-table-server>
</template>

<script>
import { roastStatusLabel, roastDegreeLabel, ROAST_STATUS_COLORS } from './roastLabels'
import RoastDegreeBadge from './DegreeBadge.vue'
import { clone, valuesIn, toPairsIn, filter, intersection } from 'lodash-es'
import qs from 'qs'

export default {
	components: { RoastDegreeBadge },
	props: {
		initQuery: { type: Object, default: null },
		title: { type: String, default: null },
		showSelect: { type: Boolean, default: false },
		singleSelect: { type: Boolean, default: true },
		headers: {
			type: Array,
			default: () => [
				{ title: '#', value: 'id' },
				{ title: '名称', align: 'left', sortable: true, value: 'name' },
				{ title: '生豆', value: 'bean_id' },
				{ title: '状态', value: 'status' },
				{ title: '', align: 'left', sortable: false, value: 'action' }
			]
		}
	},
	data() {
		const qps = qs.parse(location.search, { ignoreQueryPrefix: true })
		return {
			query: qps,
			currentQuery: {}, //当前结果的查询条件
			objects: [],
			total: 0,
			loading: false,
			options: {},
			refs: {},
			selected: [],
			errors: {}
		}
	},
	mounted() {
		if (this.initQuery) {
			this.query = clone(this.initQuery)
		}
		this.$api.common.meta('roast', 'RoastStatus', (res) => {
			this.refs = res
		})
	},
	computed: {
		queryValues() {
			return filter(valuesIn(this.query), (e) => !!e)
		},
		currentQueryValues() {
			return filter(valuesIn(this.currentQuery), (e) => !!e)
		},
		queryPairs() {
			return filter(toPairsIn(this.query), (e) => !!e[1])
		}
	},
	watch: {
		// options: {
		//   handler() {
		//     this.load()
		//   },
		//   deep: true
		// }
	},
	methods: {
		statusLabel: roastStatusLabel,
		degreeLabel: roastDegreeLabel,
		statusColor(status) {
			return ROAST_STATUS_COLORS[status] || 'default'
		},
		recipeLink(item) {
			if (!item.recipe_id) return '/roast/recipemanage'
			return `/roast/recipemanage?ids=${item.recipe_id}`
		},
		goBeanInventory(item) {
			const id = item.bean_id || item.bean?.id
			if (!id) return
			const path = `/bean/batchmanage/?bean_id=${id}`
			if (typeof this.goTo === 'function') this.goTo(path)
			else location.assign(path)
		},
		search() {
			if (
				this.queryValues.length !== this.currentQueryValues.length ||
				intersection(this.queryValues, this.currentQueryValues).length !== this.queryValues.length
			) {
				//条件变化
				if (this.options.page === 1) {
					//当前如果是第一页，手动触发
					this.load()
				} else {
					this.options.page = 1 //当前不是第一页，修改后通过watcher触发
				}
			} else {
				//条件没变，忽略
			}
		},
		reset() {
			this.query = {}
		},
		clearRecipeFilter() {
			delete this.query.recipe_id
			this.search()
		},
		clearBeanFilter() {
			delete this.query.bean_id
			this.search()
		},
		clearNoRecipeFilter() {
			delete this.query.no_recipe
			this.$emit('update:initQuery', { ...this.query })
			this.$emit('clear-no-recipe')
			this.search()
		},
		load(options = null) {
			if (!this.options) this.options = {}
			if (options) {
				this.options = options
			}

			let { sortBy, page, itemsPerPage } = this.options
			this.loading = true
			let qwp = clone(this.query)
			qwp.pageno = page
			qwp.pagesize = itemsPerPage
			qwp.sortBy = 'updated_at'
			qwp.sortDesc = 'desc'
			if (sortBy && sortBy.length > 0) {
				qwp.sort = sortBy.map((e) => `${e.key} ${e.order}`).join(',')
				qwp.sortBy = sortBy[0]['key']
				qwp.sortDesc = sortBy[0]['order']
			}
			this.currentQuery = clone(this.query)

			this.$api.roast.search(
				qwp,
				(response) => {
					this.objects = response.objects
					this.total = response.total
					this.loading = false
				},
				(error) => {
					this.errors = error.message
					this.loading = false
				}
			)
		}
	}
}
</script>

<style scoped>
.silent td {
	color: rgba(3, 3, 3, 0.5) !important;
}
</style>
